import { useMemo, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'
import { useDashboardDataset } from '@/admin/hooks/use-dashboard-config'
import type { TileDef } from './dashboard-types'
import { getLiveSource } from './live-sources'
import { HeaderControls } from './tile-header'
import { missingMapConfig } from '@/shared/components/maps/map-config'
import { GoogleHeatMap } from './heat-map-google'
import { OutlineHeatMap } from './heat-map-outline'
import { shadeStep } from './heat-map-scale'
import type { DrillHandler } from './dashboard-tiles'

/**
 * A card drawn as a heat map (WPS-232 #6): its records counted per region of
 * a country (state, province, county - any country), each region shaded
 * darker the more records it has. Records in several countries get a country
 * picker; the organisation's own country opens first. A "By product" card
 * picks a product. Clicking a region opens the records behind it (the same
 * panel as any card row), with the server's own drill so the list matches.
 */

// Five shades of the accent, lightest to darkest (theme-safe: --green only).
const SHADES = [
  'bg-[color-mix(in_srgb,var(--green)_12%,var(--bg-card))] text-[var(--ink)]',
  'bg-[color-mix(in_srgb,var(--green)_30%,var(--bg-card))] text-[var(--ink)]',
  'bg-[color-mix(in_srgb,var(--green)_50%,var(--bg-card))] text-[var(--ink)]',
  'bg-[color-mix(in_srgb,var(--green)_75%,var(--bg-card))] text-white',
  'bg-[var(--green)] text-white',
]

const ALL = '__all__'

// Google calls `window.gm_authFailure` when it rejects the Maps key for this
// page (an origin missing from the key's list, a bad key). Its map stops
// working then, so the card draws the outline map instead.
let mapsKeyRejected = false
const keyRejectedListeners = new Set<() => void>()
if (typeof window !== 'undefined') {
  const w = window as Window & { gm_authFailure?: () => void }
  const previous = w.gm_authFailure
  w.gm_authFailure = () => {
    mapsKeyRejected = true
    keyRejectedListeners.forEach((notify) => notify())
    previous?.()
  }
}

function useMapsKeyRejected(): boolean {
  return useSyncExternalStore(
    (notify) => {
      keyRejectedListeners.add(notify)
      return () => {
        keyRejectedListeners.delete(notify)
      }
    },
    () => mapsKeyRejected,
  )
}

interface Region {
  code: string
  label: string
  /** ISO alpha-2 of the region's country. */
  country: string
  value: number
  /** The drill part that opens exactly this region's records. */
  drill?: string
  /** The picker's option: a product, or a dropdown field's option label. */
  product?: string
  /** A field option's stored value (what the records are filtered on). */
  key?: string
}

interface Country {
  code: string
  label: string
  /** What the country calls its regions: "State", "Province", "County". */
  word: string
  value: number
}

interface HeatMapEnvelope {
  regions?: Region[]
  countries?: Country[]
  default_country?: string | null
  unplaced?: number
  measure_segment?: string | null
}

export function HeatMapTile({
  tile,
  createdAfter,
  onDrill,
}: {
  tile: TileDef
  createdAfter?: string | null
  onDrill: DrillHandler
}) {
  const { t } = useTranslation()
  const key = tile.data_set?.key ?? ''
  const [entity, measure] = key.split(':')
  const { data, isPending } = useDashboardDataset(key, createdAfter, 'region')
  const env = (data ?? {}) as HeatMapEnvelope
  const allRows = useMemo(() => env.regions ?? [], [env.regions])
  const countries = useMemo(() => env.countries ?? [], [env.countries])
  const unplaced = Number(env.unplaced ?? 0)
  const byField = measure?.startsWith('attr__') ?? false
  // Cards with a second dimension get a picker: By product, or By <field>.
  const byProduct = measure === 'by_product' || byField
  const [product, setProduct] = useState<string>(ALL)
  const [picked, setPicked] = useState<string | null>(null)
  const country = picked ?? env.default_country ?? countries[0]?.code ?? null
  const current = countries.find((c) => c.code === country)
  const word = (current?.word ?? t('dashboard.heatMap.regionWord', 'Region')).toLowerCase()
  // A real Google map when a Maps key is configured and Google accepts it;
  // outlines otherwise. The list is one click away on either.
  const keyRejected = useMapsKeyRejected()
  const googleReady = !missingMapConfig() && !keyRejected
  const [view, setView] = useState<'map' | 'list'>('map')

  const rows = useMemo(() => allRows.filter((r) => r.country === country), [allRows, country])

  const products = useMemo(() => {
    if (!byProduct) return []
    const totals = new Map<string, number>()
    for (const r of rows) totals.set(r.product ?? '', (totals.get(r.product ?? '') ?? 0) + r.value)
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name)
  }, [rows, byProduct])

  // One value per region for what is shown (all products, or the picked one).
  const counts = useMemo(() => {
    const m = new Map<string, { label: string; value: number; drill?: string }>()
    for (const r of rows) {
      if (byProduct && product !== ALL && r.product !== product) continue
      const cur = m.get(r.code)
      m.set(r.code, { label: r.label, value: (cur?.value ?? 0) + r.value, drill: r.drill })
    }
    return m
  }, [rows, byProduct, product])
  const max = Math.max(1, ...[...counts.values()].map((c) => c.value))
  const ranked = [...counts.entries()].sort((a, b) => b[1].value - a[1].value)
  const canOpen = !!getLiveSource(entity)
  const allLabel = byField ? t('dashboard.heatMap.allOptions', 'All') : t('dashboard.heatMap.allProducts', 'All products')
  const unit = (getLiveSource(entity)?.label ?? entity).toLowerCase()

  const open = (code: string) => {
    const c = counts.get(code)
    if (!c || !canOpen) return
    const parts = [c.drill ?? `by_region:${code}`]
    if (env.measure_segment) parts.push(env.measure_segment)
    if (byProduct && product !== ALL) {
      const key = byField ? (rows.find((r) => r.product === product)?.key ?? product) : product
      parts.push(`${byField ? measure : 'by_product'}:${key}`)
    }
    onDrill(
      tile.title ?? 'Card',
      c.label,
      [
        { l: current?.word ?? t('dashboard.heatMap.regionWord', 'Region'), v: c.label },
        ...(byProduct && product !== ALL ? [{ l: byField ? t('dashboard.heatMap.option', 'Showing') : t('dashboard.heatMap.product', 'Product'), v: product }] : []),
        { l: t('dashboard.heatMap.count', 'Count'), v: String(c.value) },
      ],
      { source: entity, segment: parts.join('|') },
    )
  }

  if (isPending) {
    return <div className="py-10 text-center text-[13px] text-[var(--ink-3)]">{t('common.loading', 'Loading...')}</div>
  }

  return (
    <div data-testid="heat-map" data-country={country ?? undefined}>
      <HeaderControls>
        <div className="flex items-center gap-2">
          {countries.length > 1 && (
            <Select value={country ?? ''} onValueChange={(v: string | null) => v && setPicked(v)}>
              <SelectTrigger className="h-9 w-[180px] text-[13px]" aria-label={t('dashboard.heatMap.pickCountry', 'Country')}>
                <SelectValue>{current?.label ?? country}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {countries.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {byProduct && products.length > 0 && (
            <Select value={product} onValueChange={(v: string | null) => setProduct(v ?? ALL)}>
              <SelectTrigger className="h-9 w-[200px] text-[13px]" aria-label={t('dashboard.heatMap.pickProduct', 'Product')}>
                <SelectValue>{product === ALL ? allLabel : product}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{allLabel}</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p || t('dashboard.heatMap.unknownProduct', 'Unknown')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="inline-flex rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-0.5">
            {(['map', 'list'] as const).map((v) => (
              <Button
                key={v}
                variant="ghost"
                aria-pressed={view === v}
                onClick={() => setView(v)}
                className={cn(
                  'h-auto rounded-full px-3 py-[3px] text-[11px] font-normal transition-all',
                  view === v
                    ? 'bg-[var(--bg-card)] text-[var(--ink)] shadow-[0_1px_2px_rgba(26,26,24,0.08)]'
                    : 'text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]',
                )}
              >
                {v === 'map' ? t('dashboard.heatMap.viewMap', 'Map') : t('dashboard.heatMap.viewList', 'List')}
              </Button>
            ))}
          </div>
        </div>
      </HeaderControls>

      {!country ? (
        <p className="py-10 text-center text-[13px] text-[var(--ink-3)]">
          {t('dashboard.heatMap.empty', 'No records with an address to place yet.')}
        </p>
      ) : view === 'map' ? (
        googleReady ? (
          <GoogleHeatMap country={country} word={word} counts={counts} max={max} unit={unit} onOpen={open} />
        ) : (
          <OutlineHeatMap
            country={country}
            countryName={current?.label ?? country}
            word={word}
            counts={counts}
            max={max}
            unit={unit}
            onOpen={open}
          />
        )
      ) : (
        <ol className="m-0 flex max-h-[380px] list-none flex-col gap-1 overflow-y-auto p-0" data-testid="heat-map-list">
          {ranked.map(([code, c]) => (
            <li key={code}>
              <Button
                type="button"
                variant="ghost"
                disabled={!canOpen}
                onClick={() => open(code)}
                data-region={code}
                className="flex h-auto w-full items-center justify-between gap-3 rounded-[6px] px-2 py-1.5 text-left text-[13px] font-normal"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className={cn('h-3 w-5 shrink-0 rounded-[3px]', SHADES[Math.max(0, shadeStep(c.value, max))])} />
                  <span className="truncate">{c.label}</span>
                </span>
                <span className="tabular-nums">{c.value.toLocaleString()}</span>
              </Button>
            </li>
          ))}
        </ol>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-[12px] text-[var(--ink-2)]">
        <div className="flex items-center gap-1.5">
          <span>{t('dashboard.heatMap.fewer', 'Fewer')}</span>
          {SHADES.map((s, i) => (
            <span key={i} className={cn('h-3 w-5 rounded-[3px]', s)} />
          ))}
          <span>{t('dashboard.heatMap.more', 'More')}</span>
        </div>
        {ranked.length > 0 && (
          <span className="tabular-nums">
            {ranked
              .slice(0, 3)
              .map(([, c]) => `${c.label} ${c.value.toLocaleString()}`)
              .join(' - ')}
          </span>
        )}
      </div>
      {unplaced > 0 && (
        <p className="mt-1 text-[11.5px] text-[var(--ink-3)]">
          {t('dashboard.heatMap.unplaced', {
            defaultValue: '{{count}} without a usable address are not on the map.',
            count: unplaced,
          })}
        </p>
      )}
    </div>
  )
}
